import { useState } from 'react';
import toast from 'react-hot-toast';
import { UserPlus } from 'lucide-react';
import { crmContactsApi } from '../../api/crm';

interface Props {
  onCreated: (contact: any) => void;
  onCancel: () => void;
  /** link the new contact to a CRM organization (e.g. the one picked on the form) */
  organizationId?: string;
}

/* Inline CRM-contact creator used by the ticket forms — create the
   customer-side person without leaving the ticket.

   Deliberately NOT a <form>: this renders inside the ticket's own <form>, and
   a nested form is invalid HTML — Enter (or the create button) could reach the
   outer form and submit/reload the whole ticket, wiping everything typed. It
   is a plain fieldset driven by explicit button clicks instead. */
export default function NewContactForm({ onCreated, onCancel, organizationId }: Props) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [saving, setSaving] = useState(false);

  const canSave = !!firstName.trim() && !!email.trim() && !saving;

  const handleCreate = async () => {
    if (!canSave) {
      toast.error('First name and email are required');
      return;
    }
    setSaving(true);
    try {
      const contact = await crmContactsApi.create({
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim(),
        phone: phone.trim() || undefined,
        company: company.trim() || undefined,
        organizationId: organizationId || undefined,
        status: 'customer',
      } as any);
      toast.success('Contact created');
      onCreated(contact);
    } catch (err: any) {
      toast.error(err.message ?? 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  // Enter anywhere in this block creates the contact — never bubbles up to the
  // ticket form's implicit submit.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    handleCreate();
  };

  return (
    <div
      onKeyDown={onKeyDown}
      className="mt-3 p-3 bg-primary-muted rounded-lg border border-primary space-y-2"
    >
      <p className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5">
        <UserPlus className="w-3.5 h-3.5" /> New Contact
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input className="input text-sm py-1.5" placeholder="First name *" value={firstName} onChange={e => setFirstName(e.target.value)} />
        <input className="input text-sm py-1.5" placeholder="Last name" value={lastName} onChange={e => setLastName(e.target.value)} />
      </div>
      <input className="input w-full text-sm py-1.5" type="email" placeholder="Email *" value={email} onChange={e => setEmail(e.target.value)} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input className="input text-sm py-1.5" placeholder="Phone" value={phone} onChange={e => setPhone(e.target.value)} />
        <input className="input text-sm py-1.5" placeholder="Company" value={company} onChange={e => setCompany(e.target.value)} />
      </div>
      <div className="flex gap-2 pt-1">
        <button type="button" className="btn btn-primary text-xs py-1.5" onClick={handleCreate} disabled={!canSave}>
          {saving ? 'Creating...' : 'Create & select'}
        </button>
        <button type="button" className="btn btn-ghost text-xs py-1.5" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}
